/**
 * The storage interface every backend implements (GitHub in production, memory for tests and the demo).
 * Paths are repo paths without a leading slash. A commit is atomic: all changes land together or not at all.
 */

export interface Author {
  name: string;
  email: string;
}

export interface FileInfo {
  path: string;
  /** Git blob sha (or any content hash) used to detect concurrent edits. */
  sha: string;
  size?: number;
}

export interface FileContent extends FileInfo {
  text: string;
}

export type FileChange =
  | { path: string; text: string; /** expected current sha; null = must not exist yet */ previousSha?: string | null }
  | { path: string; base64: string; previousSha?: string | null }
  | { path: string; delete: true; previousSha?: string };

export interface CommitInfo {
  sha: string;
  message: string;
  author: Author;
  date: string;
  /** Paths changed by this commit, when the backend knows them. */
  files?: string[];
}

export type PublishState = 'pending' | 'building' | 'live' | 'failed' | 'unknown';

export interface PublishStatus {
  state: PublishState;
  /** Link to the build log (Cloudflare), when there is one. */
  detailsUrl?: string;
  updatedAt?: string;
}

export interface CommitOptions {
  /** The commit is published right away: the host needn't build it as a preview. */
  skipPreview?: boolean;
}

export interface Storage {
  /** Files directly in `dir` (recursive when asked). Missing folder = empty list. */
  list(dir: string, options?: { recursive?: boolean }): Promise<FileInfo[]>;
  /** A text file, or null when it doesn't exist. `ref` reads an older version (commit sha). */
  read(path: string, ref?: string): Promise<FileContent | null>;
  /** Many text files at once (one request on GitHub). Missing files are left out. */
  readMany(paths: string[]): Promise<FileContent[]>;
  /** URL the browser can show for an image in the repo (blob: or API proxy URL). */
  mediaUrl(path: string): string;
  commit(changes: FileChange[], message: string, author: Author, options?: CommitOptions): Promise<CommitInfo>;
  /** Recent commits, newest first; with `path`, only those that touched it. */
  history(options?: { path?: string; limit?: number }): Promise<CommitInfo[]>;
  /** Build/publish state of a commit (from the host's check runs). */
  publishStatus(sha: string): Promise<PublishStatus>;
  /** A file's bytes as base64 (images), optionally at an older commit; null when it doesn't exist. */
  readBase64?(path: string, ref?: string): Promise<string | null>;
  /** The commit before `sha` (its first parent), or null for the first commit. */
  parentOf?(sha: string): Promise<string | null>;
}

/** A file that differs between the draft branch and the live one. */
export interface PendingFile {
  path: string;
  status: 'added' | 'modified' | 'removed';
}

export interface PendingChanges {
  files: PendingFile[];
  /** Draft commits not yet published, newest first. */
  commits: CommitInfo[];
}

/**
 * Drafts on a second branch: saves go to the draft branch (which the host builds as a preview site) and
 * publishing brings them to the live branch in one commit. The draft branch follows the live one: commits made
 * straight to live (e.g. stock updates from a shop webhook) are merged into it.
 */
export interface Drafts {
  /** Create the draft branch if missing and bring live changes into it. */
  sync(): Promise<void>;
  pending(): Promise<PendingChanges>;
  /** Publish every pending change; null when there was nothing to publish. */
  publish(message: string, author: Author): Promise<CommitInfo | null>;
  /** Throw every pending change away (the draft branch becomes the live one again). */
  discard(): Promise<void>;
}

/** The live site changed the same file as a draft, so the two can't be combined automatically. */
export class DraftConflictError extends Error {
  constructor(message = 'The live site changed a file that also has an unpublished change.') {
    super(message);
    this.name = 'DraftConflictError';
  }
}

export class ConflictError extends Error {
  constructor(public path: string) {
    super(`${path} was changed by someone else since you opened it`);
    this.name = 'ConflictError';
  }
}

export class NotFoundError extends Error {
  constructor(public path: string) {
    super(`${path} not found`);
    this.name = 'NotFoundError';
  }
}
